import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    Box,
    Alert,
    Snackbar,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import sendVerification from '../api/app/sendVerification';
import verifySmsCode from '../api/app/verifySmsCode';
import sendPasswordReset from '../api/app/sendPasswordReset';

const ForgotPasswordPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const getErrorMessage = (err, fallbackMessage) =>
        err?.response?.data?.message ||
        err?.message ||
        (typeof err === 'string' ? err : null) ||
        fallbackMessage;

    // 步驟：1=輸入手機, 2=輸入驗證碼與新密碼
    const [step, setStep] = useState(1);

    // 表單欄位
    const [phone, setPhone] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // 驗證狀態
    const [phoneError, setPhoneError] = useState(false);
    const [passwordNotMatch, setPasswordNotMatch] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // 倒數計時
    const [countdown, setCountdown] = useState(0);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

    // 驗證手機格式
    useEffect(() => {
        setPhoneError(phone && phone.length !== 10);
    }, [phone]);

    // 驗證密碼是否一致
    useEffect(() => {
        setPasswordNotMatch(newPassword && confirmPassword && newPassword !== confirmPassword);
    }, [newPassword, confirmPassword]);

    // 倒數計時
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // 發送驗證碼 (API #3: /app/auth/forget-password-sms)
    const handleSendVerification = async () => {
        if (!phone || phoneError) {
            setError('請輸入正確的手機號碼');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await sendVerification(phone);
            if (res) {
                setStep(2);
                setCountdown(60);
                setSnackbar({ open: true, msg: '驗證碼已發送', severity: 'success' });
            } else {
                setError('發送驗證碼失敗');
            }
        } catch (err) {
            setError(getErrorMessage(err, '發送驗證碼失敗'));
        } finally {
            setLoading(false);
        }
    };

    // 重新發送驗證碼
    const handleResendVerification = async () => {
        if (countdown > 0) return;
        await handleSendVerification();
    };

    // 重設密碼 (2-step: verify SMS code first, then reset password)
    const handleResetPassword = async () => {
        if (!verificationCode) {
            setError('請輸入驗證碼');
            return;
        }
        if (verificationCode.length !== 6) {
            setError('驗證碼長度應為 6 位數');
            return;
        }
        if (!newPassword || !confirmPassword) {
            setError('請輸入新密碼');
            return;
        }
        if (passwordNotMatch) {
            setError('密碼不一致');
            return;
        }
        if (newPassword.length < 6) {
            setError('密碼長度至少 6 個字元');
            return;
        }

        setLoading(true);
        setError('');
        try {
            // Step 1: Verify SMS code (API #4: /app/auth/login-by-sms-verify)
            const verifyResponse = await verifySmsCode(phone, verificationCode);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('verifySmsCode response:', verifyResponse);
            }
            if (!verifyResponse) {
                setError('驗證碼錯誤，請重新輸入');
                setLoading(false);
                return;
            }

            const token =
                verifyResponse?.token ??
                verifyResponse?.data?.token ??
                verifyResponse?.data?.data?.token ??
                verifyResponse?.access_token;

            if (!token) {
                throw new Error('SMS verified but token missing');
            }

            // Step 2: Force reset password (API #5: /app/auth/force-reset-password)
            const resetResponse = await sendPasswordReset(newPassword, { token });
            if (resetResponse !== null) {
                setSnackbar({ open: true, msg: '密碼重設成功！請重新登入', severity: 'success' });
                setTimeout(() => navigate('/app'), 2000);
            } else {
                setError('密碼重設失敗');
            }
        } catch (err) {
            setError(getErrorMessage(err, '密碼重設失敗'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                width: '100vw',
                minHeight: '100vh',
                background: theme.palette.background.default,
            }}
        >
            <AppBar
                position="static"
                elevation={0}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                }}
            >
                <Toolbar sx={{ minHeight: 56, px: 2 }}>
                    <IconButton
                        edge="start"
                        aria-label="back"
                        onClick={() => navigate(-1)}
                        sx={{ color: 'info.main' }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            flexGrow: 1,
                            textAlign: 'center',
                            fontWeight: 700,
                            color: 'text.primary',
                        }}
                    >
                        忘記密碼
                    </Typography>
                    <Box sx={{ width: 40 }} />
                </Toolbar>
            </AppBar>

            <Box maxWidth={400} mx="auto" mt={4} p={3}>
                {step === 1 && (
                    <>
                        <Typography variant="body1" mb={2} color="text.secondary">
                            請輸入您註冊時使用的手機號碼，我們將發送驗證碼給您。
                        </Typography>
                        <TextField
                            fullWidth
                            margin="dense"
                            color="secondary"
                            label="手機號碼"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            error={phoneError}
                            helperText={phoneError ? '手機號碼格式錯誤' : ''}
                            disabled={loading}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="info"
                            sx={{ mt: 3, fontWeight: 'bold', py: 1.5 }}
                            onClick={handleSendVerification}
                            disabled={loading || !phone || phoneError}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : '發送驗證碼'}
                        </Button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <Typography variant="body1" mb={2} color="text.secondary">
                            驗證碼已發送至 {phone}，請輸入驗證碼及新密碼。
                        </Typography>
                        <TextField
                            fullWidth
                            margin="dense"
                            color="secondary"
                            label="驗證碼"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            disabled={loading}
                            inputProps={{ maxLength: 6 }}
                        />
                        <Box display="flex" justifyContent="flex-end" mt={0.5}>
                            <Button
                                size="small"
                                color="info"
                                onClick={handleResendVerification}
                                disabled={countdown > 0 || loading}
                            >
                                {countdown > 0 ? `重新發送 (${countdown}s)` : '重新發送驗證碼'}
                            </Button>
                        </Box>
                        <TextField
                            fullWidth
                            margin="dense"
                            color="secondary"
                            label="新密碼"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={loading}
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            color="secondary"
                            label="確認新密碼"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={passwordNotMatch}
                            helperText={passwordNotMatch ? '密碼不一致' : ''}
                            disabled={loading}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="info"
                            sx={{ mt: 3, fontWeight: 'bold', py: 1.5 }}
                            onClick={handleResetPassword}
                            disabled={loading || !verificationCode || !newPassword || !confirmPassword || passwordNotMatch}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : '重設密碼'}
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="info"
                            sx={{ mt: 2, fontWeight: 'bold' }}
                            onClick={() => setStep(1)}
                            disabled={loading}
                        >
                            返回上一步
                        </Button>
                    </>
                )}

                {error && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ForgotPasswordPage;
